import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

const labelSx = { fontSize: 13, fontWeight: 500, color: '#aeb6c2', mb: 0.75 };

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    backgroundColor: '#1f2330',
    borderRadius: '8px',
    '& fieldset': { borderColor: '#2b3140' },
    '&:hover fieldset': { borderColor: '#2b3140' },
    '&.Mui-focused fieldset': { borderColor: '#3a54c0' }
  },
  '& .MuiInputBase-input::placeholder': { color: '#6f7a8f', opacity: 1 }
};

/**
 * In-app "Request a tool" dialog (web port of the desktop modal — replaces the
 * old external-URL button).
 *
 * Per the Figma dev note: fields are Tool name (required), Link (optional) and
 * Use case (optional); the user's email + plan are attached automatically by the
 * backend from the session, never asked for here. `onSubmit` resolves to
 * `{ status, message? }`; success swaps to a confirmation. Closes on Esc / × /
 * backdrop (MUI default via onClose).
 */
export default function RequestToolModal({ open, onClose, onSubmit, submitting }) {
  const [toolName, setToolName] = useState('');
  const [link, setLink] = useState('');
  const [useCase, setUseCase] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      setToolName('');
      setLink('');
      setUseCase('');
      setError('');
      setSubmitted(false);
    }
  }, [open]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedName = toolName.trim();
    if (!trimmedName) {
      setError("Please enter the tool you'd like us to add.");
      return;
    }
    setError('');
    const result = await onSubmit({
      toolName: trimmedName,
      link: link.trim(),
      useCase: useCase.trim()
    });
    if (result?.status) {
      setSubmitted(true);
    } else {
      setError(result?.message || "Couldn't submit your request. Please try again.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: '#181B24',
          color: 'white',
          border: '1px solid #2b3140',
          borderRadius: '12px'
        }
      }}
    >
      {submitted ? (
        <>
          <DialogContent sx={{ pt: 4 }}>
            <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: '#1f7a46',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CheckRoundedIcon sx={{ color: '#d6ffe4', fontSize: 30 }} />
              </Box>
              <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Request sent!</Typography>
              <Typography sx={{ fontSize: 14, color: '#9aa3b4', maxWidth: 340 }}>
                Thanks! We review requests every week — the most requested tools get added first.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={onClose}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: '#e7eaf2',
                backgroundColor: '#252a36',
                '&:hover': { backgroundColor: '#2c3240' }
              }}
            >
              Done
            </Button>
          </DialogActions>
        </>
      ) : (
        <form noValidate onSubmit={handleSubmit}>
          <DialogTitle
            sx={{
              fontSize: 18,
              fontWeight: 700,
              pb: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            Request a tool
            <IconButton
              onClick={onClose}
              aria-label="Close"
              size="small"
              sx={{ color: '#8b94a5', '&:hover': { color: 'white' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2.5}>
              <Typography sx={{ fontSize: 14, color: '#9aa3b4' }}>
                Tell us which tool you&apos;d like to see in KLOOW.
              </Typography>

              <Box>
                <Typography sx={labelSx}>Tool name *</Typography>
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  placeholder="e.g. Jasper"
                  value={toolName}
                  onChange={(event) => setToolName(event.target.value)}
                  inputProps={{ maxLength: 120 }}
                  error={Boolean(error)}
                  helperText={error || ' '}
                  FormHelperTextProps={{ sx: { color: '#ff7a7a', ml: 0.5 } }}
                  sx={fieldSx}
                />
              </Box>

              <Box>
                <Typography sx={labelSx}>Link to the tool (optional)</Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="https://…"
                  value={link}
                  onChange={(event) => setLink(event.target.value)}
                  inputProps={{ maxLength: 300 }}
                  sx={fieldSx}
                />
              </Box>

              <Box>
                <Typography sx={labelSx}>What would you use it for? (optional)</Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  size="small"
                  placeholder="One line is enough…"
                  value={useCase}
                  onChange={(event) => setUseCase(event.target.value)}
                  inputProps={{ maxLength: 500 }}
                  sx={fieldSx}
                />
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 1 }}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disableElevation
              disabled={submitting || toolName.trim().length === 0}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#3a54c0',
                '&:hover': { backgroundColor: '#314aad' },
                '&.Mui-disabled': { backgroundColor: '#2a3350', color: '#7a8499' }
              }}
            >
              {submitting ? 'Sending…' : 'Send request'}
            </Button>
          </DialogActions>
          <Typography sx={{ fontSize: 12, color: '#7c8597', px: 3, pb: 3, pt: 1, lineHeight: '16px' }}>
            We review requests weekly — the most requested tools get added first.
          </Typography>
        </form>
      )}
    </Dialog>
  );
}
